import { CheckCircle, Copy, FileText, Pencil, Plus } from 'lucide-react'
import { SiWebflow } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import type {
  WebflowCreateArticleResult,
  WebflowGetArticleResult,
  WebflowUpdateArticleResult,
} from 'sprites-types/workflow/tools/webflowActions'
import { webflowActions } from 'sprites-types/workflow/tools/webflowActions'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

function GetArticleDisplay({ data }: { data: WebflowGetArticleResult }) {
  const contextActions = [
    {
      id: 'copy-slug',
      label: 'Copy Slug',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        if (data.slug) {
          await copyToClipboard(data.slug)
        }
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        await copyToClipboard(data.id)
      },
    },
    {
      id: 'copy-json',
      label: 'Copy as JSON',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        await copyToClipboard(JSON.stringify(data, null, 2))
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <SiWebflow className="h-4 w-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-900">Article Retrieved</span>
            </div>
            <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 p-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="font-medium text-gray-900">{data.slug || 'No slug'}</div>
                {data.isDraft && (
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
                    Draft
                  </span>
                )}
                {data.isArchived && (
                  <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-800">
                    Archived
                  </span>
                )}
              </div>
              <div className="mt-2 text-gray-600">ID: {data.id}</div>
              {Object.keys(data.fieldData).length > 0 && (
                <div className="mt-2">
                  <div className="font-medium text-gray-700">Fields:</div>
                  <div className="mt-1 space-y-1">
                    {Object.entries(data.fieldData).map(([key, value]) => (
                      <div key={key} className="text-gray-600">
                        <span className="font-medium">{key}:</span>{' '}
                        {typeof value === 'string' && value.length > 50
                          ? `${value.substring(0, 50)}...`
                          : String(value)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function CreateArticleDisplay({ data }: { data: WebflowCreateArticleResult }) {
  const contextActions = [
    {
      id: 'copy-slug',
      label: 'Copy Slug',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        if (data.slug) {
          await copyToClipboard(data.slug)
        }
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        await copyToClipboard(data.id)
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <SiWebflow className="h-4 w-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-900">Article Created</span>
            </div>
            <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 p-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="font-medium text-gray-900">{data.slug || 'No slug'}</div>
                {data.isDraft && (
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
                    Draft
                  </span>
                )}
                {data.isArchived && (
                  <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-800">
                    Archived
                  </span>
                )}
              </div>
              <div className="mt-1 text-gray-600">ID: {data.id}</div>
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function UpdateArticleDisplay({ data }: { data: WebflowUpdateArticleResult }) {
  const contextActions = [
    {
      id: 'copy-slug',
      label: 'Copy Slug',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        if (data.slug) {
          await copyToClipboard(data.slug)
        }
      },
    },
    {
      id: 'copy-id',
      label: 'Copy ID',
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: async () => {
        await copyToClipboard(data.id)
      },
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50">
            <Pencil className="h-4 w-4 text-orange-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <SiWebflow className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">
                Article {data.updated ? 'Updated' : 'Unchanged'}
              </span>
            </div>
            <div className="mt-2 rounded-2xl border border-gray-200 bg-gray-50 p-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="font-medium text-gray-900">{data.slug || 'No slug'}</div>
                {data.isDraft && (
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
                    Draft
                  </span>
                )}
                {data.isArchived && (
                  <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-800">
                    Archived
                  </span>
                )}
              </div>
              <div className="mt-1 text-gray-600">ID: {data.id}</div>
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function WebflowActionsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const inputs = webflowActions.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return <SchemaValidationError error={inputs.error} nodeExecution={nodeExecution} />
  }

  const schema = getToolResultSchema(webflowActions, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const data = parseResult.data
  const actionName = inputs.data.action.name

  switch (actionName) {
    case 'getArticle':
      return <GetArticleDisplay data={data as WebflowGetArticleResult} />
    case 'createArticle':
      return <CreateArticleDisplay data={data as WebflowCreateArticleResult} />
    case 'updateArticle':
      return <UpdateArticleDisplay data={data as WebflowUpdateArticleResult} />
    default:
      return (
        <div className="rounded-2xl border border-gray-200/50 bg-white/70 text-black shadow-sm">
          <div className="flex items-start gap-3 px-4 py-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
              <SiWebflow className="h-4 w-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-900">Webflow Action Complete</span>
              </div>
              <div className="mt-1 text-xs text-gray-600">Action: {actionName || 'unknown'}</div>
            </div>
          </div>
        </div>
      )
  }
}

function downloadWebflowActions(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function webflowActionsToMarkdown(nodeExecution: NodeExecution): string {
  const inputs = webflowActions.argsSchema.safeParse(nodeExecution.inputs.functionArgs)

  if (!inputs.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const schema = getToolResultSchema(webflowActions, inputs.data)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const data = parseResult.data
  const actionName = inputs.data.action.name

  switch (actionName) {
    case 'getArticle': {
      const article = data as WebflowGetArticleResult
      return `## Article: ${article.slug || 'No slug'}

**Status:** ${article.isDraft ? 'Draft' : 'Published'}${article.isArchived ? ' (Archived)' : ''}
**ID:** ${article.id}

**Fields:**
${Object.entries(article.fieldData)
  .map(([key, value]) => `- **${key}:** ${String(value)}`)
  .join('\n')}

---
`
    }
    case 'createArticle': {
      const article = data as WebflowCreateArticleResult
      return `## Article Created

**Slug:** ${article.slug || 'No slug'}
**Status:** ${article.isDraft ? 'Draft' : 'Published'}${article.isArchived ? ' (Archived)' : ''}
**ID:** ${article.id}

---
`
    }
    case 'updateArticle': {
      const article = data as WebflowUpdateArticleResult
      return `## Article ${article.updated ? 'Updated' : 'Unchanged'}

**Slug:** ${article.slug || 'No slug'}
**Status:** ${article.isDraft ? 'Draft' : 'Published'}${article.isArchived ? ' (Archived)' : ''}
**ID:** ${article.id}

---
`
    }
    default:
      return `## Webflow Action: ${actionName}

\`\`\`json
${JSON.stringify(data, null, 2)}
\`\`\`

---
`
  }
}

export const WebflowActionsDisplay = createWorkflowToolComponent(
  WebflowActionsDisplayComponent,
  downloadWebflowActions,
  webflowActionsToMarkdown
)
